import React, { useEffect, useRef, useState } from 'react';
import { Icon } from '@/components/ui';
import { NAV_ITEMS, MORE_ITEMS, NavItem, filterNavItems } from './navConfig';
import { ContactSupportModal, useContactSupport } from './ContactSupportModal';

const RAIL_W = 68;

interface ShellUser {
  initials: string;
  name: string;
  isExternal: boolean;
}

interface RailSidebarProps {
  activePath: string;
  onNavigate: (path: string) => void;
  onLogout: () => void;
  user: ShellUser;
  /** Live unread counts keyed by NAV_ITEMS id (e.g. { messages: 3, notifications: 12 }). */
  counts?: Record<string, number>;
  /** /me permission flags (e.g. { canViewAdminReports: true }) that gate `requiresPermission` items. */
  permissions?: Record<string, boolean>;
}

function RailButton({
  item,
  active,
  count,
  onClick,
}: {
  item: NavItem;
  active: boolean;
  count?: number;
  onClick: () => void;
}) {
  return (
    <button
      onClick={onClick}
      title={item.label}
      style={{ width: RAIL_W }}
      className="tw-relative tw-min-h-[52px] tw-border-0 tw-bg-transparent tw-flex tw-flex-col tw-items-center tw-justify-center tw-gap-[4px] tw-cursor-pointer tw-font-poppins tw-py-[8px]"
    >
      {active && (
        <span className="tw-absolute tw-left-0 tw-top-1/2 -tw-translate-y-1/2 tw-w-[3px] tw-h-[26px] tw-bg-white tw-rounded-r-[3px]" />
      )}
      {/* "rail" indicator mode: active = white left bar + white icon only (NO filled pill).
          The translucent pill fill belongs to "pill" mode, which we don't use. */}
      <span
        className={`tw-relative tw-inline-flex tw-items-center tw-justify-center tw-w-[36px] tw-h-[30px] tw-rounded-full ${active ? '' : 'hover:tw-bg-white/[.08]'}`}
      >
        <Icon
          name={item.icon}
          size={21}
          color={active ? '#fff' : 'var(--rail-fg-muted)'}
        />
        {count != null && count > 0 && (
          <span className="tw-absolute -tw-top-[4px] tw-right-0 tw-min-w-[16px] tw-h-[16px] tw-px-[4px] tw-rounded-full tw-bg-ds-danger tw-text-white tw-text-[9.5px] tw-font-semibold tw-inline-flex tw-items-center tw-justify-center tw-border-[1.5px] tw-border-[color:var(--rail-badge-border)] tw-box-border">
            {count > 9 ? '9+' : count}
          </span>
        )}
      </span>
      <span
        className={`tw-text-[9.5px] tw-leading-[1.1] tw-text-center ${active ? 'tw-text-white tw-font-semibold' : 'tw-text-[color:var(--rail-fg-muted)] tw-font-medium'}`}
      >
        {item.label}
      </span>
    </button>
  );
}

export const RailSidebar = ({
  activePath,
  onNavigate,
  onLogout,
  user,
  counts,
  permissions,
}: RailSidebarProps) => {
  const [moreOpen, setMoreOpen] = useState(false);
  const moreRef = useRef<HTMLDivElement>(null);
  const moreItems = filterNavItems(MORE_ITEMS, {
    isExternal: user.isExternal,
    permissions,
  });
  const moreActive = moreItems.some((m) => m.path === activePath);
  const { isContactSupportOpen, openContactSupport, closeContactSupport } =
    useContactSupport();

  useEffect(() => {
    function onDoc(e: MouseEvent) {
      if (moreRef.current && !moreRef.current.contains(e.target as Node))
        setMoreOpen(false);
    }
    if (moreOpen) document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [moreOpen]);

  return (
    <>
      <nav
        style={{ width: RAIL_W }}
        className="tw-flex-none tw-bg-ds-rail tw-bg-cover tw-bg-center tw-flex tw-flex-col tw-items-center tw-h-full tw-pb-[12px] tw-relative tw-z-20"
      >
        {/* Logo: white icon-only mark served from public/images/ at /images/cloudcfo-logo-white-icon-only.png */}
        <img
          src="/images/cloudcfo-logo-white-icon-only.png"
          width={30}
          height={30}
          alt="CloudCFO"
          className="tw-mt-[16px] tw-mb-[14px]"
        />

        <div className="tw-flex tw-flex-col tw-w-full tw-gap-[2px]">
          {NAV_ITEMS.map((it) => (
            <RailButton
              key={it.id}
              item={it}
              count={counts?.[it.id] ?? it.count}
              active={activePath === it.path}
              onClick={() => onNavigate(it.path)}
            />
          ))}

          <div ref={moreRef} className="tw-relative">
            <RailButton
              item={{ id: 'more', icon: 'ellipsis', label: 'More', path: '' }}
              active={moreOpen || moreActive}
              onClick={() => setMoreOpen((v) => !v)}
            />
            {moreOpen && (
              <div
                style={{ left: RAIL_W - 4 }}
                className="tw-absolute tw-bottom-0 tw-min-w-[232px] tw-bg-white tw-border tw-border-ds-hairline tw-rounded-ds-m tw-shadow-ds-e2 tw-p-[6px] tw-z-50"
              >
                {moreItems.map((m) => {
                  const on = activePath === m.path;
                  return (
                    <button
                      key={m.id}
                      onClick={() => {
                        if (m.action === 'contact-support')
                          openContactSupport();
                        else onNavigate(m.path);
                        setMoreOpen(false);
                      }}
                      className={`tw-flex tw-items-center tw-gap-[12px] tw-w-full tw-px-[12px] tw-py-[9px] tw-border-0 tw-rounded-ds-s tw-cursor-pointer tw-text-left tw-font-poppins tw-text-[13.5px] ${on ? 'tw-bg-ds-sky-mist tw-text-ds-primary tw-font-semibold' : 'tw-bg-transparent tw-text-ds-ink-soft tw-font-medium hover:tw-bg-ds-table-header'}`}
                    >
                      <Icon
                        name={m.icon}
                        size={18}
                        color={on ? 'var(--twilight-sky)' : 'var(--ink-mute)'}
                      />
                      {m.label}
                    </button>
                  );
                })}
              </div>
            )}
          </div>
        </div>

        <div className="tw-mt-auto tw-flex tw-flex-col tw-items-center tw-gap-[10px] tw-w-full">
          <span
            style={{ width: RAIL_W - 24 }}
            className="tw-h-px tw-bg-white/[.16]"
          />
          <button
            onClick={onLogout}
            title="Log out"
            className="tw-border-0 tw-bg-transparent tw-cursor-pointer tw-flex tw-flex-col tw-items-center tw-gap-[4px] tw-p-0"
          >
            <Icon name="log-out" size={19} color="var(--rail-fg-muted)" />
            <span className="tw-text-[9.5px] tw-text-[color:var(--rail-fg-muted)] tw-font-poppins">
              Log out
            </span>
          </button>
          {/* Avatar navigates to /profile, matching the legacy header avatar (<Link to="/profile">).
            tw-border-solid + tw-m-0 counter the global button{border:none; margin-right:8px} reset. */}
          <button
            type="button"
            title={user.name}
            onClick={() => onNavigate('/profile')}
            className="tw-w-[30px] tw-h-[30px] tw-m-0 tw-p-0 tw-cursor-pointer tw-rounded-full tw-bg-white/[.18] tw-text-white tw-text-[11px] tw-font-semibold tw-inline-flex tw-items-center tw-justify-center tw-font-poppins tw-border-solid tw-border-[1.5px] tw-border-white/[.35]"
          >
            {user.initials}
          </button>
        </div>
      </nav>
      <ContactSupportModal
        isOpen={isContactSupportOpen}
        onClose={closeContactSupport}
      />
    </>
  );
};
